/**
 * Archer Arrow Effect - Arrow flight projectile and impact effects for archer attacks
 * Features:
 * - Arrow projectile flying from archer to target
 * - Trail effect behind arrow
 * - Impact dust effect at target location
 * - Arrow stick effect on hit
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';

interface ArrowProjectile {
  startX: number;
  startY: number;
  startZ: number;
  endX: number;
  endY: number;
  endZ: number;
  age: number;
  maxAge: number;
  arcHeight: number;  // Arc trajectory height
}

interface ArrowImpact {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  rotation: number;
}

interface TrailParticle {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  alpha: number;
}

const MAX_ARROWS = 100;
const MAX_IMPACTS = 50;
const MAX_TRAIL_PARTICLES = 200;

// Shader for arrow projectile
const arrowVertexShader = `
  varying vec3 vPosition;
  varying vec3 vNormal;

  void main() {
    vPosition = position;
    vNormal = normalMatrix * normal;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const arrowFragmentShader = `
  uniform vec3 shaftColor;
  uniform vec3 tipColor;

  varying vec3 vPosition;
  varying vec3 vNormal;

  void main() {
    // Simple shading
    vec3 lightDir = normalize(vec3(0.5, 1.0, 0.3));
    float diff = max(dot(normalize(vNormal), lightDir), 0.0);
    float ambient = 0.4;

    // Determine if tip or shaft based on Z position
    vec3 baseColor = vPosition.z > 0.08 ? tipColor : shaftColor;

    vec3 color = baseColor * (ambient + diff * 0.6);
    gl_FragColor = vec4(color, 1.0);
  }
`;

// Shader for trail particles
const trailVertexShader = `
  attribute float alpha;
  varying float vAlpha;

  void main() {
    vAlpha = alpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = 5.0 * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const trailFragmentShader = `
  uniform vec3 color;
  varying float vAlpha;

  void main() {
    // Circular point
    vec2 coord = gl_PointCoord - vec2(0.5);
    float dist = length(coord);
    if (dist > 0.5) discard;

    float alpha = vAlpha * (1.0 - dist * 2.0);
    gl_FragColor = vec4(color, alpha * 0.6);
  }
`;

// Shader for impact dust
const impactVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const impactFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    vec2 center = vec2(0.5, 0.5);
    float dist = distance(vUv, center) * 2.0;

    // Expanding ring
    float ringPos = progress * 0.8;
    float ringWidth = 0.3 * (1.0 - progress);
    float ring = 1.0 - smoothstep(ringPos - ringWidth, ringPos, dist)
               + smoothstep(ringPos, ringPos + ringWidth, dist);
    ring = 1.0 - ring;

    // Fade out
    float fade = 1.0 - progress;

    float alpha = ring * fade * 0.7;

    gl_FragColor = vec4(color, alpha);
  }
`;

export class ArcherArrowEffect {
  private scene: THREE.Scene;
  private time = 0;

  // Arrow projectiles
  private arrowProjectiles: ArrowProjectile[] = [];
  private arrowGeometry: THREE.BufferGeometry;
  private arrowMaterial: THREE.ShaderMaterial;
  private arrowMeshes: THREE.Mesh[] = [];

  // Trail particles
  private trailParticles: TrailParticle[] = [];
  private trailGeometry: THREE.BufferGeometry;
  private trailMaterial: THREE.ShaderMaterial;
  private trailPoints: THREE.Points | null = null;

  // Impact effects
  private arrowImpacts: ArrowImpact[] = [];
  private impactGeometry: THREE.PlaneGeometry;
  private impactMaterials: THREE.ShaderMaterial[] = [];
  private impactMeshes: THREE.Mesh[] = [];

  // Colors
  private shaftColor = new THREE.Color(0x8b4513);  // Wood brown
  private tipColor = new THREE.Color(0xa8a8a8);    // Steel gray
  private trailColor = new THREE.Color(0xccaa88); // Light tan
  private impactColor = new THREE.Color(0xaa9966); // Dust color

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Create arrow geometry (simple cylinder + cone)
    this.arrowGeometry = this.createArrowGeometry();

    // Create arrow material
    this.arrowMaterial = new THREE.ShaderMaterial({
      vertexShader: arrowVertexShader,
      fragmentShader: arrowFragmentShader,
      uniforms: {
        shaftColor: { value: this.shaftColor },
        tipColor: { value: this.tipColor },
      },
    });

    // Pre-create arrow meshes
    for (let i = 0; i < MAX_ARROWS; i++) {
      const mesh = new THREE.Mesh(this.arrowGeometry, this.arrowMaterial.clone());
      mesh.visible = false;
      mesh.castShadow = true;
      this.scene.add(mesh);
      this.arrowMeshes.push(mesh);
    }

    // Create trail geometry and material
    this.trailGeometry = new THREE.BufferGeometry();
    const trailPositions = new Float32Array(MAX_TRAIL_PARTICLES * 3);
    const trailAlphas = new Float32Array(MAX_TRAIL_PARTICLES);
    this.trailGeometry.setAttribute('position', new THREE.BufferAttribute(trailPositions, 3));
    this.trailGeometry.setAttribute('alpha', new THREE.BufferAttribute(trailAlphas, 1));

    this.trailMaterial = new THREE.ShaderMaterial({
      vertexShader: trailVertexShader,
      fragmentShader: trailFragmentShader,
      uniforms: {
        color: { value: this.trailColor },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.trailPoints = new THREE.Points(this.trailGeometry, this.trailMaterial);
    this.scene.add(this.trailPoints);

    // Create impact geometry
    this.impactGeometry = new THREE.PlaneGeometry(1, 1);

    // Pre-create impact meshes
    for (let i = 0; i < MAX_IMPACTS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: impactVertexShader,
        fragmentShader: impactFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.impactColor },
        },
        transparent: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.impactMaterials.push(material);

      const mesh = new THREE.Mesh(this.impactGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.impactMeshes.push(mesh);
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  private createArrowGeometry(): THREE.BufferGeometry {
    const arrowGroup = new THREE.Group();

    // Arrow shaft
    const shaftGeometry = new THREE.CylinderGeometry(0.02, 0.02, 0.4, 6);
    shaftGeometry.rotateX(Math.PI / 2);
    const shaft = new THREE.Mesh(shaftGeometry);
    arrowGroup.add(shaft);

    // Arrow head
    const headGeometry = new THREE.ConeGeometry(0.04, 0.1, 4);
    headGeometry.rotateX(Math.PI / 2);
    headGeometry.translate(0, 0, 0.25);
    const head = new THREE.Mesh(headGeometry);
    arrowGroup.add(head);

    // Fletching
    for (let i = 0; i < 3; i++) {
      const fletchGeometry = new THREE.BoxGeometry(0.06, 0.04, 0.01);
      const angle = (i / 3) * Math.PI * 2;
      fletchGeometry.rotateZ(angle);
      fletchGeometry.translate(Math.cos(angle) * 0.02, Math.sin(angle) * 0.02, -0.15);
      const fletch = new THREE.Mesh(fletchGeometry);
      arrowGroup.add(fletch);
    }

    // Merge into single geometry
    const geometries: THREE.BufferGeometry[] = [];
    arrowGroup.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const geo = child.geometry.clone();
        child.updateMatrix();
        geo.applyMatrix4(child.matrix);
        geometries.push(geo);
      }
    });

    // Merge geometries manually
    let totalVertices = 0;
    for (const geo of geometries) {
      totalVertices += geo.getAttribute('position').count;
    }

    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);

    let offset = 0;
    for (const geo of geometries) {
      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const count = posAttr.count;

      for (let j = 0; j < count; j++) {
        positions[(offset + j) * 3] = posAttr.getX(j);
        positions[(offset + j) * 3 + 1] = posAttr.getY(j);
        positions[(offset + j) * 3 + 2] = posAttr.getZ(j);

        if (normalAttr) {
          normals[(offset + j) * 3] = normalAttr.getX(j);
          normals[(offset + j) * 3 + 1] = normalAttr.getY(j);
          normals[(offset + j) * 3 + 2] = normalAttr.getZ(j);
        }
      }
      offset += count;
      geo.dispose();
    }

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.computeVertexNormals();

    return merged;
  }

  private setupEventListeners(): void {
    // Listen for archer attack events
    EventBus.on('unit:attack', (data) => {
      // We need the positions - get them from the event or another source
      // For now, we'll listen to a dedicated arrow event
    });

    // Listen for dedicated arrow fire event
    EventBus.on('arrow:fire', (data: {
      startX: number;
      startY: number;
      startZ: number;
      endX: number;
      endY: number;
      endZ: number;
    }) => {
      this.spawnArrow(
        data.startX, data.startY, data.startZ,
        data.endX, data.endY, data.endZ
      );
    });
  }

  spawnArrow(
    startX: number, startY: number, startZ: number,
    endX: number, endY: number, endZ: number
  ): void {
    if (this.arrowProjectiles.length >= MAX_ARROWS) {
      this.arrowProjectiles.shift();
    }

    // Calculate flight time based on distance
    const dx = endX - startX;
    const dy = endY - startY;
    const dz = endZ - startZ;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const flightTime = Math.min(0.3 + distance * 0.02, 0.8);

    // Arc height based on distance
    const arcHeight = Math.min(distance * 0.15, 2.0);

    this.arrowProjectiles.push({
      startX, startY, startZ,
      endX, endY, endZ,
      age: 0,
      maxAge: flightTime,
      arcHeight,
    });
  }

  private spawnImpact(x: number, y: number, z: number, rotation: number): void {
    if (this.arrowImpacts.length >= MAX_IMPACTS) {
      this.arrowImpacts.shift();
    }

    this.arrowImpacts.push({
      x, y, z,
      age: 0,
      maxAge: 0.4,
      rotation,
    });
  }

  private addTrailParticle(x: number, y: number, z: number): void {
    if (this.trailParticles.length >= MAX_TRAIL_PARTICLES) {
      this.trailParticles.shift();
    }

    this.trailParticles.push({
      x, y, z,
      age: 0,
      maxAge: 0.15,
      alpha: 0.8,
    });
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update arrow projectiles
    for (let i = this.arrowProjectiles.length - 1; i >= 0; i--) {
      const arrow = this.arrowProjectiles[i];
      arrow.age += deltaTime;

      if (arrow.age >= arrow.maxAge) {
        // Arrow hit - spawn impact
        this.spawnImpact(arrow.endX, arrow.endY + 0.1, arrow.endZ, Math.random() * Math.PI * 2);
        this.arrowProjectiles.splice(i, 1);
        continue;
      }

      // Calculate current position along arc
      const t = arrow.age / arrow.maxAge;

      // Linear interpolation with arc
      const x = arrow.startX + (arrow.endX - arrow.startX) * t;
      const z = arrow.startZ + (arrow.endZ - arrow.startZ) * t;

      // Parabolic arc for Y
      const baseY = arrow.startY + (arrow.endY - arrow.startY) * t;
      const arcOffset = 4 * arrow.arcHeight * t * (1 - t);  // Parabola
      const y = baseY + arcOffset;

      // Update arrow mesh
      const mesh = this.arrowMeshes[i];
      if (mesh) {
        mesh.visible = true;
        mesh.position.set(x, y, z);

        // Point arrow in direction of travel
        const nextT = Math.min(t + 0.1, 1.0);
        const nextX = arrow.startX + (arrow.endX - arrow.startX) * nextT;
        const nextZ = arrow.startZ + (arrow.endZ - arrow.startZ) * nextT;
        const nextBaseY = arrow.startY + (arrow.endY - arrow.startY) * nextT;
        const nextArcOffset = 4 * arrow.arcHeight * nextT * (1 - nextT);
        const nextY = nextBaseY + nextArcOffset;

        mesh.lookAt(nextX, nextY, nextZ);

        // Add trail particle
        if (Math.random() < 0.7) {
          this.addTrailParticle(x, y, z);
        }
      }
    }

    // Hide unused arrow meshes
    for (let i = this.arrowProjectiles.length; i < MAX_ARROWS; i++) {
      this.arrowMeshes[i].visible = false;
    }

    // Update trail particles
    const posAttr = this.trailGeometry.getAttribute('position') as THREE.BufferAttribute;
    const alphaAttr = this.trailGeometry.getAttribute('alpha') as THREE.BufferAttribute;

    for (let i = this.trailParticles.length - 1; i >= 0; i--) {
      const particle = this.trailParticles[i];
      particle.age += deltaTime;

      if (particle.age >= particle.maxAge) {
        this.trailParticles.splice(i, 1);
        continue;
      }

      const progress = particle.age / particle.maxAge;
      particle.alpha = 0.8 * (1 - progress);
    }

    // Update trail geometry
    for (let i = 0; i < MAX_TRAIL_PARTICLES; i++) {
      if (i < this.trailParticles.length) {
        const p = this.trailParticles[i];
        posAttr.setXYZ(i, p.x, p.y, p.z);
        alphaAttr.setX(i, p.alpha);
      } else {
        posAttr.setXYZ(i, 0, -100, 0);  // Hide off-screen
        alphaAttr.setX(i, 0);
      }
    }
    posAttr.needsUpdate = true;
    alphaAttr.needsUpdate = true;

    // Update impact effects
    for (let i = this.arrowImpacts.length - 1; i >= 0; i--) {
      const impact = this.arrowImpacts[i];
      impact.age += deltaTime;

      if (impact.age >= impact.maxAge) {
        this.arrowImpacts.splice(i, 1);
        continue;
      }

      const mesh = this.impactMeshes[i];
      const material = this.impactMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(impact.x, impact.y, impact.z);

        const progress = impact.age / impact.maxAge;
        const scale = 0.5 + progress * 1.0;
        mesh.scale.set(scale, scale, 1);

        material.uniforms.progress.value = progress;
      }
    }

    // Hide unused impact meshes
    for (let i = this.arrowImpacts.length; i < MAX_IMPACTS; i++) {
      this.impactMeshes[i].visible = false;
    }
  }

  dispose(): void {
    // Dispose arrow meshes
    for (const mesh of this.arrowMeshes) {
      this.scene.remove(mesh);
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    this.arrowGeometry.dispose();
    this.arrowMaterial.dispose();

    // Dispose trail
    if (this.trailPoints) {
      this.scene.remove(this.trailPoints);
    }
    this.trailGeometry.dispose();
    this.trailMaterial.dispose();

    // Dispose impacts
    for (const mesh of this.impactMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.impactMaterials) {
      material.dispose();
    }
    this.impactGeometry.dispose();

    this.arrowProjectiles = [];
    this.trailParticles = [];
    this.arrowImpacts = [];
  }
}
